/**
 * rehype plugin for a STREAMING reply: wraps each word of the block being
 * written (the last top-level element) in `<span class="amy-reveal">`, whose
 * CSS fade (src/styles/ai-elements.css) plays once, when the span is first
 * inserted.
 *
 * Why only new words animate: react-markdown keys children by tag + count
 * (`span-0`, `span-1`, …), so on every delta React keeps the spans already on
 * screen and only inserts the new ones. Why only the last block: earlier
 * blocks are complete, and leaving them as plain text bounds the extra work
 * to the paragraph / list being written. Code stays untouched.
 */

interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

const SKIP_TAGS = new Set(["code", "pre"]);

function wrapWords(node: HastNode): void {
  if (!node.children || SKIP_TAGS.has(node.tagName ?? "")) return;
  node.children = node.children.flatMap((child): HastNode[] => {
    if (child.type === "element") {
      wrapWords(child);
      return [child];
    }
    if (child.type !== "text" || !child.value) return [child];
    return child.value
      .split(/(\s+)/)
      .filter(Boolean)
      .map((part) =>
        /^\s+$/.test(part)
          ? { type: "text", value: part }
          : {
              type: "element",
              tagName: "span",
              properties: { className: ["amy-reveal"] },
              children: [{ type: "text", value: part }],
            }
      );
  });
}

export function rehypeRevealTail() {
  return (tree: HastNode): void => {
    const blocks = tree.children ?? [];
    for (let i = blocks.length - 1; i >= 0; i--) {
      if (blocks[i]!.type === "element") {
        wrapWords(blocks[i]!);
        return;
      }
    }
  };
}
